import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import DatasetMultiSelect from "components/basic/dropdowns/dataset/DatasetMultiSelect";
import InputLabelWrapper from "components/basic/input-label-wrapper/InputLabelWrapper";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import React, { useState } from "react";
import { T } from "services/lang/LanguageService";
import ChartIdentifiersEditor from "../ChartIdentifiersEditor";
import {
  DatasetScatterProps,
  YAxisIdentifier,
  YAxisIdentifierSelectionType,
  YAxisIdentifierWithOverriddenTitle,
} from "../interfaces";

interface DatasetsScatterChartDatasetsEditorProps {
  config: DatasetScatterProps;
  handleChange: (
    field: keyof DatasetScatterProps,
    value: string | string[] | number | boolean | null | YAxisIdentifier[]
  ) => void;
}

const DatasetsScatterChartDatasetsEditor = ({
  config,
  handleChange,
}: DatasetsScatterChartDatasetsEditorProps) => {
  const [isIdentifiersEditDialogVisible, setIsIdentifiersEditDialogVisible] =
    useState<boolean>(false);

  const getYAxisIdentifierValue = () => {
    if (yAxisIdentifierValue.length === 0) return "";
    const firstValue = yAxisIdentifierValue[0];
    return isYAxisIdentifierWithOverride(firstValue) ? firstValue.identifier : firstValue;
  };

  const getYAxisIdentifiers = (): YAxisIdentifier[] => {
    if (!config?.yAxisIdentifier) return [];
    return Array.isArray(config.yAxisIdentifier)
      ? config.yAxisIdentifier
      : [config.yAxisIdentifier];
  };

  const yAxisIdentifierValue = getYAxisIdentifiers();

  const isYAxisIdentifierWithOverride = (
    item: YAxisIdentifier
  ): item is YAxisIdentifierWithOverriddenTitle =>
    typeof item === "object" && item !== null && "identifier" in item;

  const handleYAxisTypeChange = (e: DropdownChangeEvent) => {
    const newValue = e.value ?? YAxisIdentifierSelectionType.MULTIPLE;

    if (
      newValue === YAxisIdentifierSelectionType.SINGLE &&
      Array.isArray(config.yAxisIdentifier) &&
      config.yAxisIdentifier.length > 1
    ) {
      confirmDialog({
        message: T("message.are-you-sure-you-want-to-select-single-type"),
        header: T("confirm-dialog.confirmation"),
        icon: "pi pi-exclamation-triangle",
        accept: () => {
          handleChange("yAxisIdentifierSelectionType", newValue);
          handleChange("yAxisIdentifier", [config.yAxisIdentifier[0]]);
        },
      });
    } else {
      handleChange("yAxisIdentifierSelectionType", newValue);
    }
  };

  return (
    <div className="grid p-fluid">
      <div className="flex flex-row w-full">
        <InputLabelWrapper labelName={T("label.scatter.x-axis-identifier")} col={12}>
          <ConfirmDialog />
          <DatasetDropdown
            value={config?.xAxisIdentifier}
            onChange={value => handleChange("xAxisIdentifier", value)}
          />
        </InputLabelWrapper>
      </div>
      <InputLabelWrapper labelName="Y axis type" col={3}>
        <Dropdown
          value={config?.yAxisIdentifierSelectionType ?? YAxisIdentifierSelectionType.MULTIPLE}
          options={Object.values(YAxisIdentifierSelectionType).map(value => ({
            label: value,
            value,
          }))}
          optionLabel="label"
          onChange={e => handleYAxisTypeChange(e)}
        />
      </InputLabelWrapper>
      <InputLabelWrapper labelName={T("label.scatter.y-axis-identifiers")} col={12}>
        <div className="flex flex-row gap-2">
          {config.yAxisIdentifierSelectionType === YAxisIdentifierSelectionType.SINGLE ? (
            <DatasetDropdown
              value={getYAxisIdentifierValue() as string}
              onChange={selectedIdentifier => {
                handleChange("yAxisIdentifier", selectedIdentifier ? [selectedIdentifier] : []);
              }}
            />
          ) : (
            <DatasetMultiSelect
              value={(yAxisIdentifierValue || [])
                .map(item => (isYAxisIdentifierWithOverride(item) ? item.identifier : item))
                .filter(item => item !== undefined)}
              onChange={selectedIdentifiers => {
                const existingIdentifiersMap = new Map(
                  (yAxisIdentifierValue || []).map(item => [
                    isYAxisIdentifierWithOverride(item) ? item.identifier : item,
                    isYAxisIdentifierWithOverride(item) ? item.overriddenTitle : "",
                  ])
                );
                const updatedIdentifiers = selectedIdentifiers.map(identifier => ({
                  identifier,
                  overriddenTitle: existingIdentifiersMap.get(identifier) || "",
                }));

                handleChange("yAxisIdentifier", updatedIdentifiers);
              }}
            />
          )}

          <Button
            icon="pi pi-pencil"
            type="button"
            className="p-button-secondary"
            tooltip={T("action.edit-identifiers")}
            onClick={() => setIsIdentifiersEditDialogVisible(true)}
          />
        </div>
      </InputLabelWrapper>
      {config.yAxisIdentifierSelectionType === YAxisIdentifierSelectionType.SINGLE && (
        <>
          <InputLabelWrapper labelName={T("label.scatter.color-axis-identifier")}>
            <DatasetDropdown
              value={config?.colorAxisIdentifier}
              onChange={value => handleChange("colorAxisIdentifier", value || null)}
            />
          </InputLabelWrapper>

          <InputLabelWrapper labelName={T("label.scatter.size-axis-identifier")}>
            <DatasetDropdown
              value={config?.sizeAxisIdentifier}
              onChange={value => handleChange("sizeAxisIdentifier", value)}
            />
          </InputLabelWrapper>
        </>
      )}
      <Dialog
        onHide={() => setIsIdentifiersEditDialogVisible(false)}
        visible={isIdentifiersEditDialogVisible}
        header={T("action.edit-identifiers")}
        style={{ width: "min(100vw, 1000px)", height: "400px" }}
      >
        <ChartIdentifiersEditor
          currentConfig={yAxisIdentifierValue}
          setCurrentConfig={identifiers => handleChange("yAxisIdentifier", identifiers)}
        />
      </Dialog>
    </div>
  );
};

export default DatasetsScatterChartDatasetsEditor;
